import React from 'react';
import { useFormikContext } from 'formik';
import { 
  Box, 
  Typography, 
  TextField, 
  Checkbox, 
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Divider,
  Paper,
  Alert
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import BusinessIcon from '@mui/icons-material/Business';
import { motion } from 'framer-motion';

import { BookingFormValues } from '../../pages/BookingPage';
import GridItem from '../common/GridItem';
import GridContainer from '../common/GridContainer';

const PaymentForm: React.FC = () => {
  const { values, errors, touched, handleChange, handleBlur } = useFormikContext<BookingFormValues>();

  return (
    <>
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Payment Information
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Payment Method</FormLabel>
          <RadioGroup
            name="paymentMethod"
            value={values.paymentMethod}
            onChange={handleChange}
            row
          >
            <FormControlLabel value="card" control={<Radio />} label="Credit Card" />
            <FormControlLabel value="corporate" control={<Radio />} label="Corporate Account" />
          </RadioGroup>
        </FormControl>
      </Box>
      
      {values.paymentMethod === 'card' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <CreditCardIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Credit Card Details</Typography>
            </Box>
            
            <GridContainer spacing={3}>
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  id="cardNumber"
                  name="cardNumber"
                  label="Card Number"
                  placeholder="XXXX XXXX XXXX XXXX"
                  value={values.cardNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cardNumber && Boolean(errors.cardNumber)}
                  helperText={touched.cardNumber && errors.cardNumber}
                />
              </GridItem>
              
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  id="cardholderName"
                  name="cardholderName"
                  label="Name on Card"
                  value={values.cardholderName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cardholderName && Boolean(errors.cardholderName)}
                  helperText={touched.cardholderName && errors.cardholderName}
                />
              </GridItem>
              
              <GridItem xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="expiryDate"
                  name="expiryDate"
                  label="Expiry Date"
                  placeholder="MM/YY"
                  value={values.expiryDate}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.expiryDate && Boolean(errors.expiryDate)}
                  helperText={touched.expiryDate && errors.expiryDate}
                />
              </GridItem>
              
              <GridItem xs={12} sm={6}>
                <TextField
                  fullWidth
                  id="cvv"
                  name="cvv"
                  label="CVV"
                  placeholder="XXX"
                  value={values.cvv}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.cvv && Boolean(errors.cvv)}
                  helperText={touched.cvv && errors.cvv}
                />
              </GridItem>
              
              <GridItem xs={12}>
                <TextField
                  fullWidth
                  id="billingZip"
                  name="billingZip"
                  label="Billing ZIP Code"
                  value={values.billingZip}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.billingZip && Boolean(errors.billingZip)}
                  helperText={touched.billingZip && errors.billingZip}
                />
              </GridItem>
              
              <GridItem xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="saveCard"
                      checked={values.saveCard}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="Save card for future bookings"
                />
              </GridItem>
            </GridContainer>
          </Paper>
        </motion.div>
      )}
      
      {values.paymentMethod === 'corporate' && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <BusinessIcon color="primary" sx={{ mr: 1 }} />
              <Typography variant="h6">Corporate Account</Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              This booking will be charged to your corporate account. An invoice will be sent to your company's billing department.
            </Alert>
            
            <TextField
              fullWidth
              id="companyName"
              name="companyName"
              label="Company Name"
              value={values.companyName}
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.companyName && Boolean(errors.companyName) && values.paymentMethod === 'corporate'}
              helperText={touched.companyName && errors.companyName && values.paymentMethod === 'corporate' ? errors.companyName : ''}
              sx={{ mb: 2 }}
            />
          </Paper>
        </motion.div>
      )}
      
      <Divider sx={{ my: 4 }} />
      
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              name="acceptTerms"
              checked={values.acceptTerms}
              onChange={handleChange}
              color="primary"
            />
          }
          label="I agree to the terms and conditions"
        />
        {touched.acceptTerms && errors.acceptTerms && (
          <Typography color="error" variant="body2">
            {errors.acceptTerms}
          </Typography>
        )}
      </Box>
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          bgcolor: 'primary.light', 
          color: 'primary.contrastText',
          borderRadius: 2,
          display: 'flex',
          alignItems: 'flex-start'
        }}
      >
        <LockIcon sx={{ mr: 2 }} />
        <Box>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
            Secure Payment
          </Typography>
          <Typography variant="body2">
            Your payment information is encrypted and securely processed. We do not store your full credit card details on our servers.
          </Typography>
        </Box>
      </Paper>
    </>
  );
};

export default PaymentForm; 